import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Download } from "react-feather";
import {
  DashboardRole,
  DashboardCategory,
  ConductConvertResponse,
  ConductConvertPerson,
  MarketMetrics,
  getDashboardConductConvert,
} from "../../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import {
  Button,
  Card,
  DescriptionList,
  EmptyState,
  Field,
  Loader,
  Select,
  Table,
  TextInput,
  Toolbar,
} from "../../../../ui";
import SegmentedControl from "./ui-pending/SegmentedControl";
import StatTile from "./ui-pending/StatTile";
import {
  roleTag,
  studentBadge,
  catChip,
  fmtPct,
  PctCell,
  dashboardErrorMessage,
  validateRange,
} from "./slotHelpers";

const today = new Date();
const iso = (d: Date) => d.toISOString().slice(0, 10);
const DEFAULT_TO = iso(today);
const DEFAULT_FROM = iso(today); // default to the current day

const ConductConvert: React.FC = () => {
  const [role, setRole] = useState<DashboardRole>("all");
  const [from, setFrom] = useState<string>(DEFAULT_FROM);
  const [to, setTo] = useState<string>(DEFAULT_TO);
  const [categoryId, setCategoryId] = useState<string>("all");
  const [data, setData] = useState<ConductConvertResponse | null>(null);
  // The response echoes back only the categories that were requested, so a filtered load would
  // otherwise shrink the dropdown to the one selected category. Keep the unfiltered list.
  const [allCategories, setAllCategories] = useState<DashboardCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const rangeErr = validateRange(from, to);
    if (rangeErr) {
      setError(rangeErr);
      toast.warning(rangeErr);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const catFilter = categoryId === "all" ? [] : [Number(categoryId)];
      const res = await getDashboardConductConvert({ from, to, role, categoryId: catFilter });
      setData(res);
      if (categoryId === "all" && res.categories?.length) setAllCategories(res.categories);
      trackEvent(MixpanelEvents.SLOT_ANALYSIS_CONDUCT_LOADED, { role, from, to, peopleCount: res.peopleCount });
    } catch (err) {
      const msg = dashboardErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [role, from, to, categoryId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Client-side CSV (UTF-8 BOM + CRLF) — matches the codebase AdminTeacherReports pattern. */
  const exportCsv = () => {
    if (!data || !data.people.length) {
      toast.warning("No data to download");
      return;
    }
    const toCsvCell = (v: string | number | null | undefined): string => {
      const str = v === null || v === undefined ? "" : String(v);
      return `"${str.replace(/"/g, '""')}"`;
    };
    const header = [
      "Role", "Name", "Category", "Paid Students",
      "US Joined", "US Conducted", "US Converted", "US Conduct%", "US Convert%",
      "ROW Joined", "ROW Conducted", "ROW Converted", "ROW Conduct%", "ROW Convert%",
      "DOM Joined", "DOM Conducted", "DOM Converted", "DOM Conduct%", "DOM Convert%",
      "Total Conducted", "Total Converted", "Total Conduct%", "Total Convert%",
    ];
    const lines = [header.map(toCsvCell).join(",")];
    data.people.forEach((p) => {
      const cells = [
        p.role, p.name, p.category, p.paidStudents ?? "",
        p.us.joined, p.us.conducted, p.us.converted, fmtPct(p.us.conductPct), fmtPct(p.us.convertPct),
        p.row.joined, p.row.conducted, p.row.converted, fmtPct(p.row.conductPct), fmtPct(p.row.convertPct),
        p.dom.joined, p.dom.conducted, p.dom.converted, fmtPct(p.dom.conductPct), fmtPct(p.dom.convertPct),
        p.total.conducted, p.total.converted, fmtPct(p.total.conductPct), fmtPct(p.total.convertPct),
      ];
      lines.push(cells.map(toCsvCell).join(","));
    });
    const csvText = `﻿${lines.join("\r\n")}`;
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `slot-analysis_conduct-convert_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    trackEvent(MixpanelEvents.SLOT_ANALYSIS_CSV_EXPORT, { from, to, role, peopleCount: data.people.length });
    toast.success("CSV downloaded");
  };

  // Category is filtered server-side (API returns categoryId + a categories list), so funnel,
  // market cards, totals and people all come straight from the response. The dropdown is the
  // exception: it reads the cached unfiltered list so selecting one category doesn't hide the rest.
  const categories = allCategories;
  const people = data?.people ?? [];
  const sortedPeople = [...people].sort((a, b) => b.total.conducted - a.total.conducted);
  const funnel = data?.funnel;
  const markets = data?.totals;

  const marketMeta: { k: "us" | "row" | "dom"; label: string }[] = [
    { k: "us", label: "🇺🇸 US" },
    { k: "row", label: "🌏 ROW" },
    { k: "dom", label: "🇮🇳 Domestic" },
  ];

  const roleOptions: { value: DashboardRole; label: string }[] = [
    { value: "all", label: "All" },
    { value: "AE", label: "AE" },
    { value: "TE", label: "TE" },
  ];
  const categoryOptions = [
    { value: "all", label: "All Categories" },
    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  const marketCells = (v: MarketMetrics) => {
    return (
      <>
        <td className="ds-num">{v.joined || "—"}</td>
        <td className="ds-num"><span className="ds-strong">{v.conducted}</span></td>
        <td className="ds-num sa-text--te"><span className="ds-strong">{v.converted}</span></td>
        <PctCell pct={v.conductPct} type="conduct" />
        <PctCell pct={v.convertPct} type="convert" />
      </>
    );
  };

  return (
    <div className="ds-stack sa-stack">
      {/* Filters */}
      <Card className="sa-filters">
        <Toolbar>
          <Field label="From">
            {(ctl) => <TextInput {...ctl} type="date" size="sm" value={from} onChange={(e) => setFrom(e.target.value)} />}
          </Field>
          <Field label="To">
            {(ctl) => <TextInput {...ctl} type="date" size="sm" value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
          <Field label="Role" className="sa-toolbar__auto">
            {(ctl) => <SegmentedControl<DashboardRole> id={ctl.id} aria-label="Role" options={roleOptions} value={role} onChange={setRole} />}
          </Field>
          <Field label="Category">
            {(ctl) => <Select {...ctl} size="sm" options={categoryOptions} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} />}
          </Field>
          <div className="ds-toolbar__end">
            <Button size="sm" icon={<Download size={14} aria-hidden />} onClick={exportCsv} disabled={loading || !people.length}>
              Export CSV
            </Button>
          </div>
        </Toolbar>
      </Card>

      {loading ? (
        <Loader label="Loading conduct & convert…" />
      ) : error ? (
        <EmptyState title="Couldn't load the dashboard" description={error} />
      ) : !data ? (
        <EmptyState title="No data" description="Pick a date range to load the funnel." />
      ) : (
        <>
          {/* Funnel */}
          <Card title="Overall funnel">
            <div className="sa-funnel">
              <StatTile label="Scheduled" value={funnel?.totalScheduled ?? 0} hint="baseline" />
              <span className="sa-funnel__arrow" aria-hidden>→</span>
              <StatTile tone="primary" label="Joined" value={funnel?.joined ?? 0} hint={`${fmtPct(funnel?.joinPct)} join rate`} />
              <span className="sa-funnel__arrow" aria-hidden>→</span>
              <StatTile tone="primary" label="Conducted" value={funnel?.conducted ?? 0} hint={`${fmtPct(funnel?.conductPct)} conduct rate`} />
              <span className="sa-funnel__arrow" aria-hidden>→</span>
              <StatTile tone="success" label="Converted" value={funnel?.converted ?? 0} hint={`${fmtPct(funnel?.convertPct)} conversion rate`} />
              {/* Leakage, not a funnel stage — joined trials that never got an AE. */}
              <span className="sa-funnel__gap" aria-hidden />
              <StatTile tone="warning" label="No AE found" value={funnel?.noAeFound ?? 0} hint={`of ${funnel?.joined ?? 0} joined`} />
            </div>
            <div className="sa-markets">
              {marketMeta.map((m) => {
                const v = markets?.[m.k] as MarketMetrics | undefined;
                return (
                  <Card key={m.k} title={m.label} className="sa-market">
                    <DescriptionList
                      items={[
                        { label: "Scheduled", value: <span className="ds-num">{v?.totalScheduled ?? 0}</span> },
                        { label: "Joined", value: <span className="ds-num">{v?.joined ?? 0}</span> },
                        { label: "Join %", value: <span className="ds-num sa-text--ae">{fmtPct(v?.joinPct)}</span> },
                        { label: "Conducted", value: <span className="ds-num">{v?.conducted ?? 0}</span> },
                        { label: "Conduct %", value: <span className="ds-num sa-text--ae">{fmtPct(v?.conductPct)}</span> },
                        { label: "Converted", value: <span className="ds-num">{v?.converted ?? 0}</span> },
                        { label: "Convert %", value: <span className="ds-num sa-text--te">{fmtPct(v?.convertPct)}</span> },
                        {
                          label: "No AE found",
                          value: <span className={`ds-num${(v?.noAeFound ?? 0) > 0 ? " sa-text--dom" : ""}`}>{v?.noAeFound ?? 0}</span>,
                        },
                        { label: "AE conducted", value: <span className="ds-num sa-text--ae">{v?.aeConducted ?? 0}</span> },
                        { label: "TE conducted", value: <span className="ds-num sa-text--te">{v?.teConducted ?? 0}</span> },
                      ]}
                    />
                  </Card>
                );
              })}
            </div>
          </Card>

          {/* Per-person table */}
          <Card title="AE / TE breakdown" flush>
            {!sortedPeople.length ? (
              <EmptyState title="No AEs or TEs in this range" description="Try a wider date range or another role or category." />
            ) : (
              <Table caption="Conduct and convert per AE / TE, by market" className="sa-cc-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>Role</th>
                    <th rowSpan={2}>Name</th>
                    <th rowSpan={2}>Category</th>
                    <th rowSpan={2}>Students</th>
                    <th colSpan={5} className="sa-group">🇺🇸 US</th>
                    <th colSpan={5} className="sa-group">🌏 ROW</th>
                    <th colSpan={5} className="sa-group">🇮🇳 Domestic</th>
                    <th colSpan={4} className="sa-group">Total</th>
                  </tr>
                  <tr>
                    {["Joined", "Conducted", "Converted", "Conduct%", "Convert%"].map((h) => <th key={`us-${h}`} className="ds-num">{h}</th>)}
                    {["Joined", "Conducted", "Converted", "Conduct%", "Convert%"].map((h) => <th key={`row-${h}`} className="ds-num">{h}</th>)}
                    {["Joined", "Conducted", "Converted", "Conduct%", "Convert%"].map((h) => <th key={`dom-${h}`} className="ds-num">{h}</th>)}
                    {["Conducted", "Converted", "Conduct%", "Convert%"].map((h) => <th key={`tot-${h}`} className="ds-num">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {sortedPeople.map((p: ConductConvertPerson) => (
                    <tr key={p.teacherId} className={p.role === "AE" ? "sa-row--ae" : "sa-row--te"}>
                      <td>{roleTag(p.role)}</td>
                      <td>{p.name}</td>
                      <td>{catChip(p.category)}</td>
                      <td>{studentBadge(p.role, p.paidStudents)}</td>
                      {marketCells(p.us)}
                      {marketCells(p.row)}
                      {marketCells(p.dom)}
                      <td className="ds-num"><span className="ds-strong">{p.total.conducted}</span></td>
                      <td className="ds-num sa-text--te"><span className="ds-strong">{p.total.converted}</span></td>
                      <PctCell pct={p.total.conductPct} type="conduct" /><PctCell pct={p.total.convertPct} type="convert" />
                    </tr>
                  ))}
                </tbody>
                {markets && (
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="sa-total-label">Total · {people.length} people</td>
                      {[markets.us, markets.row, markets.dom].map((v, i) => (
                        <React.Fragment key={i}>
                          <td className="ds-num">{v.joined}</td>
                          <td className="ds-num">{v.conducted}</td>
                          <td className="ds-num sa-text--te">{v.converted}</td>
                          <PctCell pct={v.conductPct} type="conduct" />
                          <PctCell pct={v.convertPct} type="convert" />
                        </React.Fragment>
                      ))}
                      <td className="ds-num">{markets.total.conducted}</td>
                      <td className="ds-num sa-text--te">{markets.total.converted}</td>
                      <PctCell pct={markets.total.conductPct} type="conduct" /><PctCell pct={markets.total.convertPct} type="convert" />
                    </tr>
                  </tfoot>
                )}
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default ConductConvert;
